import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const EMPTY_FORM = {
  id: null,
  code: '',
  description: '',
  discount_type: 'percent',
  discount_value: '',
  min_order_value: '0',
  max_discount_value: '',
  usage_limit: '',
  usage_limit_per_user: '1',
  starts_at: '',
  expires_at: '',
  status: 'active',
};

function toDateTimeLocal(value) {
  if (!value) return '';
  return String(value).replace(' ', 'T').slice(0, 16);
}

export function CouponsPage({
  loading,
  error,
  data,
  filters,
  onFilter,
  onCreateCoupon,
  onUpdateCoupon,
  onDeleteCoupon,
  saving,
  deleting,
  canManage,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const coupons = data?.items || [];

  const resetForm = () => setForm(EMPTY_FORM);

  const submit = async (event) => {
    event.preventDefault();
    const payload = {
      code: form.code,
      description: form.description,
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      min_order_value: Number(form.min_order_value || 0),
      max_discount_value: form.max_discount_value === '' ? null : Number(form.max_discount_value),
      usage_limit: form.usage_limit === '' ? null : Number(form.usage_limit),
      usage_limit_per_user: form.usage_limit_per_user === '' ? null : Number(form.usage_limit_per_user),
      starts_at: form.starts_at || null,
      expires_at: form.expires_at || null,
      status: form.status,
    };

    if (form.id) {
      await onUpdateCoupon(form.id, payload);
    } else {
      await onCreateCoupon(payload);
    }
    resetForm();
  };

  if (loading) return <LoadingBlock label="Carregando cupons..." />;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="page-grid two-columns">
      <SectionCard
        title="Cupons"
        subtitle="Descontos comerciais com validade, limite de uso e controle por cliente."
        actions={canManage ? <button className="ghost-button" type="button" onClick={resetForm}>Novo cupom</button> : null}
      >
        <div className="callout-box compact-box admin-callout">
          <span className="eyebrow">Loja</span>
          <strong>Campanhas de desconto controladas</strong>
          <p>Crie cupom percentual ou valor fixo, limite por usuário e data de expiração para evitar desconto solto no pedido.</p>
        </div>

        <div className="toolbar-row">
          <select value={filters.status || 'all'} onChange={(event) => onFilter({ ...filters, status: event.target.value })}>
            <option value="all">Todos</option>
            <option value="active">Ativos</option>
            <option value="paused">Pausados</option>
            <option value="expired">Expirados</option>
          </select>
        </div>

        <DataTable
          rows={coupons}
          columns={[
            { key: 'code', label: 'Código' },
            { key: 'discount_value', label: 'Desconto', render: (row) => row.discount_type === 'percent' ? `${Number(row.discount_value).toFixed(0)}%` : `R$ ${Number(row.discount_value).toFixed(2)}` },
            { key: 'min_order_value', label: 'Mínimo', render: (row) => `R$ ${Number(row.min_order_value || 0).toFixed(2)}` },
            { key: 'used_count', label: 'Usos' },
            { key: 'status', label: 'Status', type: 'status' },
            {
              key: 'actions',
              label: 'Ação',
              render: (row) => canManage ? (
                <div className="inline-actions">
                  <button className="inline-button" type="button" onClick={() => setForm({
                    id: row.id,
                    code: row.code || '',
                    description: row.description || '',
                    discount_type: row.discount_type || 'percent',
                    discount_value: String(row.discount_value ?? ''),
                    min_order_value: String(row.min_order_value ?? 0),
                    max_discount_value: row.max_discount_value ?? '',
                    usage_limit: row.usage_limit ?? '',
                    usage_limit_per_user: row.usage_limit_per_user ?? '',
                    starts_at: toDateTimeLocal(row.starts_at),
                    expires_at: toDateTimeLocal(row.expires_at),
                    status: row.status || 'active',
                  })}>Editar</button>
                  <button className="inline-button danger-text" type="button" onClick={() => onDeleteCoupon(row.id)} disabled={deleting}>Remover</button>
                </div>
              ) : 'Somente leitura',
            },
          ]}
        />
      </SectionCard>

      <SectionCard title={form.id ? 'Editar cupom' : 'Novo cupom'} subtitle="Cupom pronto para ser usado no checkout/pedido.">
        {canManage ? (
          <form className="admin-form" onSubmit={submit}>
            <div className="form-row">
              <label>
                Código
                <input value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })} placeholder="PERIFA10" required />
              </label>
              <label>
                Status
                <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
                  <option value="active">Ativo</option>
                  <option value="paused">Pausado</option>
                  <option value="expired">Expirado</option>
                </select>
              </label>
            </div>
            <label>
              Descrição
              <input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Campanha de lançamento" />
            </label>
            <div className="form-row triple">
              <label>
                Tipo
                <select value={form.discount_type} onChange={(event) => setForm({ ...form, discount_type: event.target.value })}>
                  <option value="percent">Percentual</option>
                  <option value="fixed">Valor fixo</option>
                </select>
              </label>
              <label>
                Valor do desconto
                <input type="number" step="0.01" min="0" value={form.discount_value} onChange={(event) => setForm({ ...form, discount_value: event.target.value })} required />
              </label>
              <label>
                Pedido mínimo
                <input type="number" step="0.01" min="0" value={form.min_order_value} onChange={(event) => setForm({ ...form, min_order_value: event.target.value })} />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Desconto máximo
                <input type="number" step="0.01" min="0" value={form.max_discount_value} onChange={(event) => setForm({ ...form, max_discount_value: event.target.value })} />
              </label>
              <label>
                Limite total
                <input type="number" min="1" value={form.usage_limit} onChange={(event) => setForm({ ...form, usage_limit: event.target.value })} />
              </label>
              <label>
                Limite por usuário
                <input type="number" min="1" value={form.usage_limit_per_user} onChange={(event) => setForm({ ...form, usage_limit_per_user: event.target.value })} />
              </label>
            </div>
            <div className="form-row">
              <label>
                Início
                <input type="datetime-local" value={form.starts_at} onChange={(event) => setForm({ ...form, starts_at: event.target.value })} />
              </label>
              <label>
                Expira em
                <input type="datetime-local" value={form.expires_at} onChange={(event) => setForm({ ...form, expires_at: event.target.value })} />
              </label>
            </div>
            <div className="inline-actions">
              <button className="primary-button" type="submit" disabled={saving}>{saving ? 'Salvando...' : form.id ? 'Salvar cupom' : 'Criar cupom'}</button>
              {form.id ? <button className="inline-button" type="button" onClick={resetForm}>Cancelar edição</button> : null}
            </div>
          </form>
        ) : <div className="empty-state">Seu perfil pode consultar cupons, mas não pode gerenciar campanhas.</div>}
      </SectionCard>
    </div>
  );
}
